// Admin actions for the per-diet logbook list: create a logbook for a member
// and override a logbook's status. Both are privilege-guarded at the call site
// in AdminDietLogbooksPage via <AccessGate>. The backend enforces the same
// privileges (application_diet.create / application_diet.activate) independently.
import { useEffect, useRef, useState } from "react";
import { Plus, X, Loader2, Search } from "lucide-react";
import {
  useLazyAdminMemberLookupQuery,
  useAdminCreateLogbookMutation,
  useAdminUpdateLogbookStatusMutation,
} from "../api/adminLogbookApi";

// Only the statuses the DB enum actually stores — must match the backend.
export const ADMIN_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "in_progress", label: "In Progress" },
  { value: "submitted", label: "Submitted" },
  { value: "graded", label: "Graded" },
  { value: "repeating", label: "Repeating" },
  { value: "completed", label: "Completed" },
];

const STAGE_OPTIONS = [1, 2, 3];

function errMsg(e: any, fallback: string): string {
  return e?.data?.message || e?.error || fallback;
}

/* ------------------------------------------------------------------ */
/* Create logbook                                                      */
/* ------------------------------------------------------------------ */

export function CreateLogbookButton({
  dietId,
  dietTitle,
}: {
  dietId: string;
  dietTitle?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700"
      >
        <Plus className="w-4 h-4" /> Create logbook
      </button>
      {open && <CreateLogbookModal dietId={dietId} dietTitle={dietTitle} onClose={() => setOpen(false)} />}
    </>
  );
}

function CreateLogbookModal({
  dietId,
  dietTitle,
  onClose,
}: {
  dietId: string;
  dietTitle?: string;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [picked, setPicked] = useState<any | null>(null);
  const [stage, setStage] = useState(1);
  const [status, setStatus] = useState("in_progress");
  // Optional result — filled when the admin records a graded outcome outright.
  const [totalScore, setTotalScore] = useState("");
  const [finalGrade, setFinalGrade] = useState("");
  const [remarks, setRemarks] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // Grading fields only make sense once the logbook has an outcome.
  const showResult = status === "graded" || status === "completed";

  const [triggerLookup, { data: results = [], isFetching }] = useLazyAdminMemberLookupQuery();
  const [createLogbook, { isLoading: creating }] = useAdminCreateLogbookMutation();

  // Debounce the typeahead.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (debouncedQ.length >= 2) triggerLookup(debouncedQ);
  }, [debouncedQ, triggerLookup]);

  const submit = async () => {
    setErr(null);
    setOk(null);
    if (!picked) {
      setErr("Select a member first.");
      return;
    }
    if (showResult && totalScore !== "") {
      const n = Number(totalScore);
      if (Number.isNaN(n) || n < 0 || n > 100) {
        setErr("Total score must be a number between 0 and 100.");
        return;
      }
    }
    try {
      await createLogbook({
        user_id: picked.user_id,
        logbook_diet_id: dietId,
        stage,
        status,
        // Only send result fields when they apply — a graded/completed outcome.
        total_score: showResult && totalScore !== "" ? Number(totalScore) : undefined,
        final_grade: showResult && finalGrade.trim() ? finalGrade.trim() : undefined,
        remarks: showResult && remarks.trim() ? remarks.trim() : undefined,
      }).unwrap();
      setOk(`Stage ${stage} logbook created for ${picked.name}.`);
      // Reset for a possible next create; keep the modal open so the admin sees confirmation.
      setPicked(null);
      setQ("");
      setDebouncedQ("");
      setTotalScore("");
      setFinalGrade("");
      setRemarks("");
    } catch (e) {
      setErr(errMsg(e, "Could not create the logbook."));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Create logbook</h2>
            {dietTitle && <p className="text-xs text-gray-500 mt-0.5">{dietTitle}</p>}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Member picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Member</label>
            {picked ? (
              <div className="flex items-center justify-between border rounded-lg px-3 py-2 bg-gray-50">
                <div>
                  <div className="text-sm font-medium text-gray-900">{picked.name}</div>
                  <div className="text-xs text-gray-500">{picked.membership_no || picked.email}</div>
                </div>
                <button onClick={() => setPicked(null)} className="text-xs text-indigo-600 hover:underline">
                  Change
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name, email or membership no…"
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
                />
                {debouncedQ.length >= 2 && (
                  <div className="mt-1 border rounded-lg max-h-52 overflow-y-auto">
                    {isFetching ? (
                      <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Searching…
                      </div>
                    ) : results.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">No members found.</div>
                    ) : (
                      results.map((m: any) => (
                        <button
                          key={m.user_id}
                          onClick={() => {
                            setPicked(m);
                            setOk(null);
                            setErr(null);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 border-b last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{m.name}</div>
                          <div className="text-xs text-gray-500">{m.membership_no || m.email}</div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Stage + status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
              <select
                value={stage}
                onChange={(e) => setStage(Number(e.target.value))}
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-100"
              >
                {STAGE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    Stage {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-100"
              >
                {ADMIN_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Result — shown only when the chosen status is a graded outcome. */}
          {showResult && (
            <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-3">
              <div className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Result</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total score</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={totalScore}
                    onChange={(e) => setTotalScore(e.target.value)}
                    placeholder="0 – 100"
                    className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Outcome / grade</label>
                  <input
                    value={finalGrade}
                    onChange={(e) => setFinalGrade(e.target.value)}
                    placeholder="e.g. SUCCESSFUL"
                    className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-100"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remark</label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows={3}
                  placeholder="Assessment remark…"
                  className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-100 resize-y"
                />
              </div>
            </div>
          )}

          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{err}</div>}
          {ok && <div className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">{ok}</div>}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">
            Close
          </button>
          <button
            onClick={submit}
            disabled={creating || !picked}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
          >
            {creating && <Loader2 className="w-4 h-4 animate-spin" />}
            Create logbook
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Row status editor                                                   */
/* ------------------------------------------------------------------ */

export function RowStatusEditor({ logbookId, status }: { logbookId: string; status?: string }) {
  const [updateStatus, { isLoading }] = useAdminUpdateLogbookStatusMutation();
  const current = String(status ?? "in_progress").toLowerCase();
  const selectRef = useRef<HTMLSelectElement>(null);

  const onChange = async (next: string) => {
    if (next === current) return;
    const label = ADMIN_STATUS_OPTIONS.find((o) => o.value === next)?.label ?? next;
    if (!window.confirm(`Set this logbook's status to "${label}"?`)) {
      if (selectRef.current) selectRef.current.value = current;
      return;
    }
    try {
      await updateStatus({ id: logbookId, status: next }).unwrap();
    } catch (e) {
      alert(errMsg(e, "Could not update the status."));
      if (selectRef.current) selectRef.current.value = current;
    }
  };

  // If the current value isn't one of the editable options (a legacy status),
  // show it as a disabled leading option so we never silently rewrite it.
  const known = ADMIN_STATUS_OPTIONS.some((o) => o.value === current);

  return (
    <div className="inline-flex items-center gap-1.5">
      <select
        ref={selectRef}
        defaultValue={current}
        disabled={isLoading}
        onChange={(e) => onChange(e.target.value)}
        className="border rounded-md px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-gray-100 disabled:opacity-50"
      >
        {!known && (
          <option value={current} disabled>
            {current.replace(/_/g, " ")}
          </option>
        )}
        {ADMIN_STATUS_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
    </div>
  );
}
